import React, { useContext } from 'react';
import { styled } from '@compiled/react';

import { token } from '@atlaskit/tokens';

import { useContentState } from '@confluence/content-state/entry-points/useContentState.ts';
import type { ReactionLocation } from '@confluence/reactions/entry-points/reactionTypes.ts';
import { ReactionContainerType } from '@confluence/reactions/entry-points/reactionTypes.ts';
import { useReactionsQueryParams } from '@confluence/reactions/entry-points/useReactionsQueryParams.ts';
import { DelayedReactions, Reactions } from '@confluence/reactions/entry-points/Reactions.ts';
import { ReactionsPlaceholder } from '@confluence/reactions/entry-points/ReactionsPlaceholder.tsx';
import { ReactionsContext } from '@confluence/comment-context/entry-points/ReactionsContext.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useRenderServerPlaceholder } from '@confluence/ssr-utilities/entry-points/useRenderServerPlaceholder.ts';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ViewPageReactionsContainer = styled.div({
	display: 'flex',
	alignItems: 'flex-start',
	justifyContent: 'space-between',
	padding: `${token('space.250')} 0`,
	clear: 'both',
	borderBottom: `${token('border.width')} solid ${token('color.border')}`,
	width: '100%',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'.labelsSection': {
		maxWidth: '50%',
		minWidth: '20%',
		float: 'right',
		display: 'inline-block',
	},
	overflow: 'visible',
	maxWidth: 'unset',
	margin: 'unset',
});

export const ViewPageReactions = ({
	hasReactions,
	contentType,
	contentId,
	spaceId,
	contentSubType,
}: {
	hasReactions: any;
	contentType: any;
	contentId: any;
	spaceId: any;
	contentSubType: any;
}): React.JSX.Element | null => {
	const [state] = useContentState();

	const { focusedPageReactionsId } = useReactionsQueryParams();
	const { isReactionsEnabled } = useContext(ReactionsContext);
	const { edition } = useSessionData();
	const renderServerPlaceholder = useRenderServerPlaceholder();
	const displayReactions = () => {
		if (contentType && spaceId) {
			// We want to load in the background ONLY if we have a query param, otherwise we can preload the query
			const ReactionsLoader = focusedPageReactionsId ? DelayedReactions : Reactions;

			return (
				<ReactionsLoader
					contentId={contentId}
					location={contentType as ReactionLocation}
					containerId={spaceId}
					containerType={ReactionContainerType.SPACE}
					contentSubType={contentSubType}
					customAnalyticsAttributes={{
						edition,
						reactionsPlacement: 'bottom',
					}}
				/>
			);
		} else {
			return <ReactionsPlaceholder />;
		}
	};
	if (renderServerPlaceholder) {
		return (
			<ViewPageReactionsContainer
				id="view-page-reactions-container"
				data-vc="view-page-reactions-container-server-placeholder"
				data-testid="view-page-reactions-container"
				data-test-id="view-page-reactions-container"
			>
				{isReactionsEnabled && hasReactions && <ReactionsPlaceholder useAccuratePills />}
			</ViewPageReactionsContainer>
		);
	} else if (state?.isContentReady) {
		return (
			<ViewPageReactionsContainer
				id="view-page-reactions-container"
				data-vc="view-page-reactions-container"
				data-testid="view-page-reactions-container"
				data-test-id="view-page-reactions-container"
			>
				{hasReactions && isReactionsEnabled && displayReactions()}
			</ViewPageReactionsContainer>
		);
	}

	return null;
};
